/**
 * Shared, SDK-agnostic Sentry configuration used by BOTH init sites:
 *   - the browser SDK in `app/entry.client.tsx`
 *   - the Cloudflare Worker SDK in `worker/app.ts`
 *
 * Keeping the noise-filtering rules and environment logic in one place stops
 * the two inits from drifting (they drifted badly before this).
 *
 * IMPORTANT: this module must stay pure data + pure functions with NO SDK,
 * browser, or node imports, so it bundles cleanly for both the client and the
 * worker runtimes.
 */

export type SentryEnvironment = 'development' | 'preview' | 'production';

/**
 * Resolve the deploy environment from an env value. Anything we don't
 * recognise (including the common case of it being unset during local
 * `remix vite:dev`) falls back to `development`, which disables Sentry.
 */
export function resolveSentryEnvironment(value: string | undefined | null): SentryEnvironment {
  if (value === 'production' || value === 'preview') {
    return value;
  }

  return 'development';
}

/**
 * Sentry is intentionally silent in development so local work never pollutes
 * production signal (confirmed with the team, 2026-07).
 */
export function isSentryEnabled(environment: SentryEnvironment): boolean {
  return environment !== 'development';
}

/**
 * Full traces in preview (low volume, useful for debugging); sampled down in
 * production to cut volume and quota. Was `1` everywhere before — the single
 * biggest source of noise/cost.
 */
export function sentryTracesSampleRate(environment: SentryEnvironment): number {
  return environment === 'production' ? 0.15 : 1;
}

/**
 * Error messages that are never actionable for us — dropped before they ever
 * become a Sentry issue. Matched by Sentry against the error message/type.
 */
export const SENTRY_IGNORE_ERRORS: (string | RegExp)[] = [
  // Benign browser layout warnings
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',

  // Non-error rejections carry no stack and no signal
  'Non-Error promise rejection captured',

  // User navigated away / cancelled an in-flight request — expected
  'AbortError',
  'The user aborted a request',
  'The operation was aborted',

  // Transient network hiccups (offline, flaky wifi) — not our bug
  /Load failed/i, // Safari's fetch-abort message
  'NetworkError when attempting to fetch resource',
  'Failed to fetch',
];

/**
 * Stack-frame origins that are not our code (browser extensions, internal
 * browser pages). Events whose top frame comes from these are dropped.
 */
export const SENTRY_DENY_URLS: RegExp[] = [
  /extensions\//i,
  /^chrome:\/\//i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /safari-web-extension:/i,
];

/**
 * Shared `beforeSend` predicate. Returns `true` when the event should be
 * DROPPED. Kept as a predicate (not a full beforeSend) so each SDK can wrap it
 * with its own event/hint shape.
 *
 * @param firstException `event.exception?.values?.[0]`
 * @param originalException the thrown value from the SDK hint
 */
export function shouldDropSentryEvent(
  firstException: { type?: string; value?: string } | undefined,
  originalException: unknown,
): boolean {
  // Ad-blocker blocked our own outbound request — expected, not a bug.
  // (Preserves the original entry.client behaviour.)
  if (firstException?.type === 'NetworkError' && firstException.value?.includes('ERR_BLOCKED_BY_CLIENT')) {
    return true;
  }

  // Handled HTTP 4xx (except 429 rate-limit, which we DO want to see) are
  // expected responses we deal with in code, not crashes. 5xx still reports.
  const status =
    (originalException as { status?: unknown })?.status ?? (originalException as { statusCode?: unknown })?.statusCode;

  if (typeof status === 'number' && status >= 400 && status < 500 && status !== 429) {
    return true;
  }

  return false;
}
